import { useMemo } from "react";
import { useDerivedSnapshot } from "@/lib/derivedIntel/derivedHook";
import { rateConfidence, type ConfidenceRating } from "@/lib/viralIntel/confidenceEngine";
import { detectContrarianAlerts } from "@/lib/viralIntel/contrarianEngine";
import { detectHeatWindows } from "@/lib/viralIntel/heatWindowEngine";
import { deriveStoryArc, type StoryArcFrame } from "@/lib/viralIntel/storyArcEngine";
import { synthesizeCommunityInsights } from "@/lib/viralIntel/communitySentiment";
import {
  coordinateEmotionalIntensity,
  type EmotionalDirective,
  type DominantKind,
} from "@/lib/viralIntel/emotionalIntensityCoordinator";
import {
  useDelayedBySignal,
  type EmotionalSignalKind,
} from "@/lib/sync/offsetOrchestration";

/**
 * usePublicEmotionalSignal
 * ------------------------
 * Single arbiter hook for the PUBLIC (non-admin) emotional layer.
 *
 * - Reuses the existing DerivedIntelligenceEngine snapshot (no new polling).
 * - Runs ALL emotional engines (heat / story arc / contrarian / community /
 *   confidence) deterministically.
 * - Funnels everything through EmotionalIntensityCoordinator so the public
 *   UI only ever renders ONE dominant signal at a time (no spam).
 * - Applies offsetOrchestration per-kind so ultra-live signals stay fast
 *   while spoiler-prone ones respect the TV offset.
 */

export interface PublicEmotionalCtx {
  homeLabel: string;
  awayLabel: string;
  scoreHome: number;
  scoreAway: number;
  /** Optional crowd inputs — pass what the page already knows; everything else is omitted. */
  community?: {
    goalBeliefPct?: number;
    hypeIndex?: number;
    hypeMomentum?: number;
    activeVoters?: number;
    reactionRatePerMin?: number;
    comebackBeliefPct?: number;
    panicIndex?: number;
    underdogSupportPct?: number;
    bettingCrowdBias?: number;
  };
  lang?: "ES" | "EN" | "PT";
}

export interface PublicEmotionalSignal {
  /** True when the coordinator has nothing meaningful to show. */
  idle: boolean;
  kind: DominantKind;
  headline: string;
  detail: string;
  intensity: number;
  suppressNoise: boolean;
  rating: ConfidenceRating;
  arc: StoryArcFrame;
}

const KIND_TO_SIGNAL: Record<Exclude<DominantKind, "idle">, EmotionalSignalKind> = {
  heat_window: "heat_window",
  story_arc: "story_arc",
  contrarian: "contrarian",
  viral_card: "viral_card",
  crowd: "crowd",
};

export function usePublicEmotionalSignal(
  ctx: PublicEmotionalCtx,
): PublicEmotionalSignal {
  const snap = useDerivedSnapshot(2500);

  const coordCtx = useMemo(
    () => ({
      homeLabel: ctx.homeLabel,
      awayLabel: ctx.awayLabel,
      scoreHome: ctx.scoreHome,
      scoreAway: ctx.scoreAway,
    }),
    [ctx.homeLabel, ctx.awayLabel, ctx.scoreHome, ctx.scoreAway],
  );

  const rating = useMemo(() => rateConfidence(snap), [snap]);
  const arc = useMemo(() => deriveStoryArc(snap, coordCtx), [snap, coordCtx]);
  const heat = useMemo(() => detectHeatWindows(snap), [snap]);
  const contrarian = useMemo(
    () => detectContrarianAlerts(snap, coordCtx),
    [snap, coordCtx],
  );
  const insights = useMemo(
    () =>
      ctx.community
        ? synthesizeCommunityInsights(
            snap,
            ctx.community,
            { homeLabel: ctx.homeLabel, awayLabel: ctx.awayLabel, lang: ctx.lang ?? "ES" },
          )
        : [],
    [snap, ctx.community, ctx.homeLabel, ctx.awayLabel, ctx.lang],
  );

  const directive: EmotionalDirective = useMemo(
    () =>
      coordinateEmotionalIntensity({
        snap,
        rating,
        arc,
        heat,
        contrarian,
        insights,
      }),
    [snap, rating, arc, heat, contrarian, insights],
  );

  // Time-shift the dominant slot only — secondaries are suppressed in public
  // anyway. limited_sample never reaches the public UI.
  const signalKind: EmotionalSignalKind | null =
    directive.dominant.kind === "idle"
      ? null
      : KIND_TO_SIGNAL[directive.dominant.kind];

  const delayedDominant = useDelayedBySignal(
    directive.dominant,
    signalKind ?? "confidence",
  );

  const idle =
    directive.dominant.kind === "idle" ||
    rating.level === "limited_sample" ||
    delayedDominant.intensity < 0.45;

  return {
    idle,
    kind: delayedDominant.kind,
    headline: delayedDominant.headline,
    detail: delayedDominant.detail,
    intensity: delayedDominant.intensity,
    suppressNoise: directive.suppressNoise,
    rating,
    arc,
  };
}
